import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { vehicleName } from '../lib/utils';
import { useMonthOptions } from '../lib/useMonthOptions';
import { Loader2, Car, ChevronRight, ChevronDown, ChevronUp, CheckCircle2, TrendingUp, Calendar } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

function fmt(v) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v); }
function fmt2(v) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v); }

export default function MobileCommissions() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mois, setMois] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [showDetail, setShowDetail] = useState(false);
    const monthOptions = useMonthOptions();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/commissions?mois=${mois}`, { withCredentials: true });
            setData(res.data.length > 0 ? res.data[0] : null);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [mois]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

    return (
        <div className="px-5 pt-[calc(1rem+env(safe-area-inset-top))] pb-6" data-testid="mobile-commissions">
            <h1 className="text-xl font-bold text-gray-900 mb-1">Mes commissions</h1>

            {/* Month selector */}
            <div className="mb-5">
                <Select value={mois} onValueChange={setMois}>
                    <SelectTrigger className="w-full bg-white border-gray-200 text-[13px] h-10 rounded-xl shadow-sm" data-testid="mobile-month-select">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" /><SelectValue />
                    </SelectTrigger>
                    <SelectContent>{monthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
            </div>

            {!data || (data.nb_ventes === 0 && !data.total_sav_direct && !data.report_sav_entrant && !data.report_sav_sortant) ? (
                <div className="text-center py-16">
                    <p className="text-sm text-gray-400">Aucune vente pour cette période</p>
                </div>
            ) : (
                <div className="space-y-4 animate-fade-in">
                    {/* Commission card */}
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="mobile-commission-card">
                        <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Commission estimée</p>
                        <p className={`text-3xl font-bold font-mono ${data.commission >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(data.commission)}</p>
                        <p className="text-[12px] text-gray-400 mt-1">{data.nb_ventes} vente{data.nb_ventes > 1 ? 's' : ''} · Taux {data.taux_applique}%</p>
                    </div>

                    {/* Progress toward threshold */}
                    {data.seuil > 0 && (
                        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="mobile-progress-card">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-[12px] text-gray-500 font-medium">Progression vers le seuil</span>
                                <span className="text-[13px] font-mono font-semibold text-gray-800">{fmt(data.marge_nette)} / {fmt(data.seuil)}</span>
                            </div>
                            <div className="w-full h-2.5 rounded-full bg-gray-100 overflow-hidden mb-2">
                                <div className={`h-full rounded-full transition-all duration-700 ${data.progress_pct >= 100 ? 'bg-emerald-500' : 'bg-[#6366F1]'}`}
                                     style={{ width: `${Math.min(100, data.progress_pct)}%` }} />
                            </div>
                            <div className="flex items-center justify-between">
                                <span className={`text-[12px] font-semibold font-mono ${data.progress_pct >= 100 ? 'text-emerald-600' : 'text-gray-500'}`}>
                                    {data.progress_pct.toFixed(0)}%
                                </span>
                                {data.progress_pct >= 100 ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                                        <CheckCircle2 className="w-3 h-3" />Seuil atteint
                                    </span>
                                ) : (
                                    <span className="text-[11px] text-gray-400">
                                        Encore <span className="font-mono font-medium text-gray-600">{fmt(data.seuil - data.marge_nette)}</span> avant {data.taux_haut}%
                                    </span>
                                )}
                            </div>
                            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                                <span className="text-[12px] text-gray-500">Taux appliqué</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-[14px] font-mono font-bold text-gray-900">{data.taux_applique}%</span>
                                    {data.progress_pct < 100 && (
                                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                            <TrendingUp className="w-3 h-3" />{data.taux_haut}% dès {fmt(data.seuil)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Detail toggle */}
                    <button onClick={() => setShowDetail(!showDetail)}
                        className="w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center justify-between active:scale-[0.98] transition-transform"
                        data-testid="mobile-detail-toggle">
                        <span className="text-[13px] font-medium text-gray-700">Calcul détaillé</span>
                        {showDetail ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </button>

                    {showDetail && (
                        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-3 animate-fade-in" data-testid="mobile-detail-content">
                            <Row label="Marge brute véhicules" value={fmt2(data.marge_brute_vehicules)} />
                            {data.total_retenues_sav > 0 && <Row label="SAV imputés" value={`− ${fmt2(data.total_retenues_sav)}`} color="text-red-500" />}
                            {data.total_frais_bureau > 0 && <Row label="Frais de bureau" value={`− ${fmt2(data.total_frais_bureau)}`} color="text-red-500" />}
                            <div className="border-t border-gray-100 pt-2">
                                <Row label="Marge nette" value={fmt2(data.marge_nette)} bold />
                            </div>
                            <Row label="Seuil" value={fmt(data.seuil)} />
                            <Row label="Taux bas / haut" value={`${data.taux_bas}% / ${data.taux_haut}%`} />
                            <Row label="Taux appliqué" value={`${data.taux_applique}%`} bold />
                            <Row label="Commission théorique" value={fmt2(data.commission_theorique || 0)} />
                            {data.total_reduction_min_agence > 0 && <Row label={`Réduction min. agence (${fmt(data.marge_min_agence || 750)}/véh.)`} value={`− ${fmt2(data.total_reduction_min_agence)}`} color="text-amber-600" />}
                            <div className="border-t border-gray-100 pt-2 space-y-1" data-testid="mobile-reconciliation">
                                {data.historique && <Row label="Premier calcul enregistré" value={fmt2(data.historique.commission)} color={data.historique.ecart ? 'text-amber-600' : 'text-gray-500'} />}
                                {data.vehicules_multi_commerciaux?.length > 0 && <p className="text-[11px] text-red-700" data-testid="mobile-double">{data.vehicules_multi_commerciaux.length} véhicule(s) aussi compté(s) pour un autre commercial</p>}
                                <Row label="Somme des commissions des véhicules" value={fmt2(data.somme_commissions_vehicules || 0)} />
                                {(data.ajustements || []).map(a => <Row key={a.code} label={a.libelle} value={fmt2(a.montant)} color={a.montant < 0 ? 'text-red-500' : 'text-gray-500'} />)}
                            </div>
                            <div className="border-t border-gray-100 pt-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-[13px] text-gray-900 font-semibold">{data.provisoire ? 'Commission provisoire' : 'Commission finale'}</span>
                                    <span className={`text-lg font-bold font-mono ${data.commission >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt2(data.commission)}</span>
                                </div>
                                {data.provisoire && <p className="text-[11px] text-amber-700" data-testid="mobile-provisoire">Provisoire — frais en cours de régularisation</p>}
                            </div>
                        </div>
                    )}

                    {/* Vehicle list */}
                    {data.vehicles && data.vehicles.length > 0 && (
                        <div>
                            <h2 className="text-sm font-semibold text-gray-900 mb-2">Ventes du mois</h2>
                            <div className="space-y-2">
                                {data.vehicles.map(v => (
                                    <MobileSale key={v.id} v={v} data={data} navigate={navigate} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function MobileSale({ v, data, navigate }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="bg-white rounded-xl p-3.5 shadow-sm border border-gray-100" data-testid={`mobile-comm-vehicle-${v.id}`}>
            <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 text-left" data-testid={`mobile-sale-toggle-${v.id}`}>
                <Car className="w-4 h-4 text-gray-400 shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-gray-700 truncate">{vehicleName(v)}</p>
                    <p className="text-[10px] text-gray-400 font-mono">{v.immatriculation} · {v.date_vente}</p>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-[13px] font-mono font-semibold text-emerald-600">{fmt2(v.commission_finale || 0)}</p>
                    <p className="text-[10px] text-gray-400 font-mono">marge {fmt(v.marge_nette_ht)} · {v.taux_applique}%</p>
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-gray-300" /> : <ChevronDown className="w-4 h-4 text-gray-300" />}
            </button>
            {open && (
                <div className="mt-2 pt-2 border-t border-gray-100 space-y-1" data-testid={`mobile-sale-detail-${v.id}`}>
                    <Row label="Marge nette HT" value={fmt2(v.marge_nette_ht)} />
                    <Row label={`Théorique à ${data.taux_bas}% / ${data.taux_haut}%`} value={`${fmt2(v.commission_40 || 0)} / ${fmt2(v.commission_50 || 0)}`} />
                    <Row label={`Théorique (${v.taux_applique}%)`} value={fmt2(v.commission_theorique || 0)} />
                    {v.reduction_min_agence > 0 && <Row label="Réduction min. agence" value={`− ${fmt2(v.reduction_min_agence)}`} color="text-amber-600" />}
                    <Row label="Commission finale" value={fmt2(v.commission_finale || 0)} bold />
                    <button onClick={() => navigate(`/calculator/${v.id}`)} className="text-[12px] text-[#6366F1] font-medium flex items-center gap-1 pt-1">Ouvrir la fiche<ChevronRight className="w-3.5 h-3.5" /></button>
                </div>
            )}
        </div>
    );
}

function Row({ label, value, color = 'text-gray-700', bold }) {
    return (
        <div className="flex justify-between gap-3 text-[13px]">
            <span className={`text-gray-500 ${bold ? 'font-medium' : ''}`}>{label}</span>
            <span className={`font-mono whitespace-nowrap ${color} ${bold ? 'font-semibold' : ''}`}>{value}</span>
        </div>
    );
}
